import { Alert, FormControl, Stack } from "@mui/material";
import { Box } from "@mui/system";
import { useState } from "react";

import { ApiEnumName } from "@/common/services/enum";
import { DateRange } from "@/common/ts/datetime";
import { assertNever } from "@/common/ts/typeHelpers";
import {
  AppPermission,
  BaseEntityDto,
  ScheduleEntryEntityType,
  VehicleDto,
} from "@/core/api/generated/v1";

import AuthorizedElement from "../../Auth/AuthorizedElement";
import Datetime from "../../Datetime/Datetime";
import AccessoryCheckCreateUpdate from "../../Entity/AccessoryCheck/AccessoryCheckCreateUpdate";
import ContractCreateUpdate from "../../Entity/Contract/ContractCreateUpdate";
import DamageCostEvaluationCreateUpdate from "../../Entity/DamageCostEvaluation/DamageCostEvaluationCreateUpdate";
import DamageDetectionCreateUpdate from "../../Entity/DamageDetection/DamageDetectionCreateUpdate";
import MaintenanceCreateUpdate from "../../Entity/Maintenance/MaintenanceCreateUpdate";
import RepairOperationCreateUpdate from "../../Entity/RepairOperation/RepairOperationCreateUpdate";
import VehicleLink from "../../Entity/Vehicle/VehicleLink";
import VisualInspectionCreateUpdate from "../../Entity/VisualInspection/VisualInspectionCreateUpdate";
import WashCreateUpdate from "../../Entity/Wash/WashCreateUpdate";
import WheelOperationCreateUpdate from "../../Entity/WheelOperation/WheelOperationCreateUpdate";
import ApiEnumAutocomplete from "../../Enum/ApiEnumAutocomplete";
import InlineApiEnumValue from "../../Enum/InlineApiEnumValue";
import InfoTile from "../../Form/Display/InfoTile";
import AppTypography from "../../Text/AppTypography";

const allowedEntryEntityTypesMap: Record<ScheduleEntryEntityType, boolean> = {
  [ScheduleEntryEntityType.None]: false,
  [ScheduleEntryEntityType.Contract]: true,
  [ScheduleEntryEntityType.AssetSubscription]: false, // TODO
  [ScheduleEntryEntityType.AccessoryCheck]: true,
  [ScheduleEntryEntityType.VisualInspection]: true,
  [ScheduleEntryEntityType.DamageDetection]: true,
  [ScheduleEntryEntityType.DamageCostEvaluation]: true,
  [ScheduleEntryEntityType.RepairOperation]: true,
  [ScheduleEntryEntityType.Wash]: true,
  [ScheduleEntryEntityType.WheelOperation]: true,
  [ScheduleEntryEntityType.Maintenance]: true,
  [ScheduleEntryEntityType.GeneralStatusHistory]: false,
};
const allowedEntryEntityTypes = Object.entries(allowedEntryEntityTypesMap)
  .filter(([key, value]) => value === true)
  .map(([key, value]) => key) as ScheduleEntryEntityType[];

export interface VehicleScheduleEntryCreateDefaultValues {
  vehicleId: Nil<string>;
  vehicle: Nil<VehicleDto>;
  dateRange: Nil<Partial<DateRange>>;
  entryEntityType?: ScheduleEntryEntityType;
}

export interface VehicleScheduleEntryCreateProps {
  defaultValues: VehicleScheduleEntryCreateDefaultValues;
  onCancel: () => void;
  onSave: (params: { entryEntityType: ScheduleEntryEntityType; newEntity: BaseEntityDto }) => void;
}

export default function VehicleScheduleEntryCreate({
  defaultValues,
  onCancel,
  onSave,
}: VehicleScheduleEntryCreateProps) {
  const vehicleId = defaultValues.vehicleId || undefined;

  const [entryEntityType, setEntryEntityType] = useState<ScheduleEntryEntityType | null>(
    defaultValues.entryEntityType || null,
  );

  const handleSave = (newEntity: BaseEntityDto) => {
    if (!entryEntityType) {
      return;
    }
    onSave({ entryEntityType: entryEntityType, newEntity: newEntity });
  };

  const renderEntityCreateForm = () => {
    if (!entryEntityType || !allowedEntryEntityTypesMap[entryEntityType]) {
      return null;
    }

    switch (entryEntityType) {
      case ScheduleEntryEntityType.None:
        return null;
      case ScheduleEntryEntityType.Contract:
        return (
          <AuthorizedElement permissions={[AppPermission.ContractManage]}>
            <ContractCreateUpdate
              defaultValues={{
                vehicleId: vehicleId,
                startsAt: defaultValues.dateRange?.start?.toISOString(),
                endsAt: defaultValues.dateRange?.end?.toISOString(),
              }}
              onSave={(newValue) => handleSave(newValue)}
              onCancel={onCancel}
            />
          </AuthorizedElement>
        );
      case ScheduleEntryEntityType.AssetSubscription: // TODO
        return null;
      case ScheduleEntryEntityType.AccessoryCheck:
        return (
          <AuthorizedElement
            permissionsAny={[
              AppPermission.AccessoryCheckPerform,
              AppPermission.AccessoryCheckManage,
            ]}
          >
            <AccessoryCheckCreateUpdate
              defaultValues={{
                vehicleId: vehicleId,
                inspectedAt: defaultValues.dateRange?.start?.toISOString(),
              }}
              onSave={(newValue) => handleSave(newValue)}
              onCancel={onCancel}
            />
          </AuthorizedElement>
        );
      case ScheduleEntryEntityType.VisualInspection:
        return (
          <AuthorizedElement
            permissionsAny={[
              AppPermission.VisualInspectionPerform,
              AppPermission.VisualInspectionManage,
            ]}
          >
            <VisualInspectionCreateUpdate
              defaultValues={{
                vehicleId: vehicleId,
                inspectedAt: defaultValues.dateRange?.start?.toISOString(),
              }}
              onSave={(newValue) => handleSave(newValue)}
              onCancel={onCancel}
            />
          </AuthorizedElement>
        );
      case ScheduleEntryEntityType.DamageDetection:
        return (
          <AuthorizedElement
            permissionsAny={[
              AppPermission.DamageDetectionPerform,
              AppPermission.DamageDetectionManage,
            ]}
          >
            <DamageDetectionCreateUpdate
              defaultValues={{
                vehicleId: vehicleId,
                inspectedAt: defaultValues.dateRange?.start?.toISOString(),
              }}
              onSave={(newValue) => handleSave(newValue)}
              onCancel={onCancel}
            />
          </AuthorizedElement>
        );
      case ScheduleEntryEntityType.DamageCostEvaluation:
        return (
          <AuthorizedElement
            permissionsAny={[
              AppPermission.DamageCostEvaluationPerform,
              AppPermission.DamageCostEvaluationManage,
            ]}
          >
            <DamageCostEvaluationCreateUpdate
              defaultValues={{
                vehicleId: vehicleId,
                inspectedAt: defaultValues.dateRange?.start?.toISOString(),
              }}
              onSave={(newValue) => handleSave(newValue)}
              onCancel={onCancel}
            />
          </AuthorizedElement>
        );
      case ScheduleEntryEntityType.RepairOperation:
        return (
          <AuthorizedElement
            permissionsAny={[
              AppPermission.RepairOperationPerform,
              AppPermission.RepairOperationManage,
            ]}
          >
            <RepairOperationCreateUpdate
              defaultValues={{
                vehicleId: vehicleId,
                date: defaultValues.dateRange?.start?.toISOString(),
              }}
              onSave={(newValue) => handleSave(newValue)}
              onCancel={onCancel}
            />
          </AuthorizedElement>
        );
      case ScheduleEntryEntityType.Wash:
        return (
          <AuthorizedElement permissionsAny={[AppPermission.WashPerform, AppPermission.WashManage]}>
            <WashCreateUpdate
              defaultValues={{
                vehicleId: vehicleId,
                scheduledAt: defaultValues.dateRange?.start?.toISOString(),
              }}
              onSave={(newValue) => handleSave(newValue)}
              onCancel={onCancel}
            />
          </AuthorizedElement>
        );
      case ScheduleEntryEntityType.WheelOperation:
        return (
          <AuthorizedElement
            permissionsAny={[
              AppPermission.WheelOperationPerform,
              AppPermission.WheelOperationManage,
            ]}
          >
            <WheelOperationCreateUpdate
              defaultValues={{
                vehicleId: vehicleId,
                scheduledAt: defaultValues.dateRange?.start?.toISOString(),
              }}
              onSave={(newValue) => handleSave(newValue)}
              onCancel={onCancel}
            />
          </AuthorizedElement>
        );
      case ScheduleEntryEntityType.Maintenance:
        return (
          <AuthorizedElement
            permissionsAny={[AppPermission.MaintenancePerform, AppPermission.MaintenanceManage]}
          >
            <MaintenanceCreateUpdate
              defaultValues={{
                vehicleId: vehicleId,
                scheduledAt: defaultValues.dateRange?.start?.toISOString(),
              }}
              onSave={(newValue) => handleSave(newValue)}
              onCancel={onCancel}
            />
          </AuthorizedElement>
        );
      case ScheduleEntryEntityType.GeneralStatusHistory:
        return null;
      default:
        assertNever(entryEntityType);
    }
  };

  const entityCreateForm = renderEntityCreateForm();

  return (
    <Stack gap={2}>
      {/* Base info and inputs. */}
      <Stack gap={1}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xxs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(4, 1fr)",
            },
            gridTemplateRows: "auto",
            rowGap: 1,
            columnGap: 1,
          }}
        >
          <InfoTile label='Vehicle' iconOf='vehicle' isEmpty={!defaultValues.vehicleId}>
            <VehicleLink
              entityId={defaultValues.vehicleId}
              entity={defaultValues.vehicle}
              inlineProps={{ withIcon: false, withTooltip: false }}
            />
          </InfoTile>

          <InfoTile label='Start' iconOf='date' isEmpty={!defaultValues.dateRange?.start}>
            <Datetime datetime={defaultValues.dateRange?.start} />
          </InfoTile>

          <InfoTile label='End' iconOf='date' isEmpty={!defaultValues.dateRange?.end}>
            <Datetime datetime={defaultValues.dateRange?.end} />
          </InfoTile>
        </Box>

        <Box>
          <FormControl fullWidth margin='dense'>
            <ApiEnumAutocomplete
              type={ApiEnumName.ScheduleEntryEntityType}
              onlyEnumValues={allowedEntryEntityTypes}
              value={entryEntityType}
              label='Entity type'
              required
              onChange={(newValue) => {
                if (entryEntityType) {
                  const confirmed = window.confirm("Discard unsaved changes?");
                  if (!confirmed) return;
                }
                setEntryEntityType(newValue || null);
              }}
            />
          </FormControl>
        </Box>
      </Stack>

      {/* Entity create form  */}
      {entryEntityType && (
        <Box>
          {!entityCreateForm && (
            <Alert severity='error'>
              <InlineApiEnumValue
                type={ApiEnumName.ScheduleEntryEntityType}
                value={entryEntityType}
              />{" "}
              creation is not allowed!
            </Alert>
          )}

          {entityCreateForm && (
            <Stack gap={1}>
              <AppTypography variant='h5'>
                New{" "}
                <strong>
                  <InlineApiEnumValue
                    type={ApiEnumName.ScheduleEntryEntityType}
                    value={entryEntityType}
                    isInLowerCase
                  />
                </strong>
                :
              </AppTypography>

              {entityCreateForm}
            </Stack>
          )}
        </Box>
      )}
    </Stack>
  );
}
